import { IF, IS, nil, NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { UITooltip } from "uicore-ts/compiledScripts/UITooltip"
import { CBDataViewCellDescriptor } from "../CB/CBDataView"
import { CBDataViewRow, CBTableRowView } from "../CB/CBTableRowView"
import { RowView } from "../RowView"
import { RHColor } from "./RHColor"
import { RHTooltip } from "./RHTooltip"
import { RHTables } from "./Utils_slop/RHDesignTokens"

/**
 * RHTableRowView
 * Styles: Bootstrap .table .table-hover
 *
 * When `usesCardMode` is enabled, below `cardModeBreakpoint` logical pixels wide
 * the row switches to a vertical card layout: each column becomes a (label : value)
 * strip. The default RH table presentation stays tabular and relies on shared
 * column minimums + horizontal grid scrolling instead.
 */
export class RHTableRowView<DataType extends object> extends CBTableRowView<DataType> {
    
    usesCardMode = NO
    cardModeBreakpoint = 640
    treeDisclosureColumnKeyPath?: string

    private _cardLabelViews: UITextView[] = []
    _treeDisclosureColumnBaseLeftInset?: number
    _treeDisclosureColumnBaseRightInset?: number
    _treeConnectorStemView?: UIView
    _treeConnectorHorizontalView?: UIView
    _cpvDisclosurePresentationKey?: string


    constructor(elementID: string) {
        
        super(elementID)
        
        ;(this as RHTableRowView<DataType>).configureWithObject({
            padding: 0
        })
        
        this.backgroundView.configureWithObject({
            style: {
                borderBottom: "none"
            }
        })
        this._bottomLineView.hidden = YES

        this._treeConnectorStemView = new UIView(this.elementID + "TreeConnectorStemView")
        this._treeConnectorStemView.configureWithObject({
            backgroundColor: RHTables.treeConnectorColor,
            hidden: YES,
            userInteractionEnabled: NO
        })
        this.addSubview(this._treeConnectorStemView)
        
        this._treeConnectorHorizontalView = new UIView(this.elementID + "TreeConnectorHorizontalView")
        this._treeConnectorHorizontalView.configureWithObject({
            backgroundColor: RHTables.treeConnectorColor,
            hidden: YES,
            userInteractionEnabled: NO
        })
        this.addSubview(this._treeConnectorHorizontalView)
        
        this.usesVirtualLayoutingForIntrinsicSizing = YES
        
    }
    
    
    override setKeyboardFocusedCellIndex(index: number | undefined) {
        this._keyboardFocusedCellIndex = index
        this.backgroundView.backgroundColor = IF(index !== undefined)(() =>
            new UIColor("rgba(0, 100, 255, 0.07)")
        ).ELSE(() =>
            UIColor.clearColor
        )
        // Only row-level tint — no individual cell pressed appearance
    }
    
    
    override set descriptors(descriptors: CBDataViewCellDescriptor<DataType>[]) {
        
        super.descriptors = descriptors
        // These optional caches are read through `??`; uicore's `nil` is a Proxy
        // object and would suppress the fallback to the cells' real base insets.
        this._treeDisclosureColumnBaseLeftInset = undefined
        this._treeDisclosureColumnBaseRightInset = undefined
        
        this._cardLabelViews.forEach(lv => lv.removeFromSuperview())
        this._cardLabelViews = []

        const cellStyle = {
            borderRight: "none",
            borderLeft: "none"
        }

        this.dataCells.forEach(cell => {
            
            cell.minHeight = RHTables.rowMinHeight
            
            if (cell.variant == "label") {
                cell.contentInsets = {
                    ...cell.contentInsets,
                    bottom: this.core.paddingLength * 0.5,
                    top: this.core.paddingLength * 0.5
                }
                cell.titleLabel!.configureWithObject({
                    style: {
                        fontSize: RHTables.rowFontSize,
                        fontFamily: RHTables.rowFontFamily,
                        fontWeight: "400"
                    }
                })
            }
            else if (cell.variant == "button") {
                cell.buttonView!.titleLabel.configureWithObject({
                    style: {
                        fontSize: RHTables.rowFontSize,
                        fontFamily: RHTables.rowFontFamily,
                        fontWeight: RHTables.rowButtonFontWeight
                    }
                })
            }
            
            cell.configureWithObject({ style: cellStyle })

        })

        if (NO == this.usesCardMode) {
            return
        }
        
        descriptors.forEach(descriptor => {
            let titleString = ""
            if (typeof descriptor.title === "string") {
                titleString = descriptor.title
            }
            const lv = new UITextView(undefined, UITextView.type.span)
                .configuredWithObject({
                    text: titleString,
                    isSingleLine: YES,
                    textColor: RHColor.textSecondary,
                    style: {
                        fontSize: "0.78em",
                        fontWeight: "500",
                        letterSpacing: "0.02em"
                    }
                }) as UITextView
            this.addSubview(lv)
            this._cardLabelViews.push(lv)
        })
        
    }
    
    override get descriptors() {
        return super.descriptors
    }
    
    
    override set data(data: CBDataViewRow<DataType> | undefined) {
        
        super.data = data

        this._updateCellTooltips()

    }

    override get data() {
        return super.data
    }


    override _updateDisclosureControlPresentation() {
        if (this._usesCPVTreePresentation()) {
            this._updateCPVDisclosureControlPresentation()
            return
        }
        if (this._treeDisclosureColumnIndex() >= 0) {
            this._updateEmphasizedDisclosureControlPresentation()
            return
        }
        super._updateDisclosureControlPresentation()
    }


    _treeDisclosureColumnIndex() {
        if (!this.treeDisclosureColumnKeyPath) {
            return -1
        }
        return this.visibleDescriptors.findIndex(descriptor =>
            descriptor.keyPath == this.treeDisclosureColumnKeyPath
        )
    }

    /**
     * Gives every data cell a clean, RH-styled hover tooltip. CBTableRowView sets
     * each cell's native `title` to the raw key-path value (raw ISO dates,
     * "[object Object]" for object/array columns), which we drop here. The tooltip
     * shows the descriptor's custom text when provided, otherwise the cell's own
     * formatted display text (with the date <br> and any inline markup removed).
     *
     * Button-variant cells (e.g. the Jah/Ei evaluation pill, per-row action
     * buttons) get the same treatment on their button: UIButton falls back to
     * the raw label text for its native title on every layout pass, so a
     * markup label would otherwise show raw HTML in the hover tooltip. Setting
     * the button's hoverText to the plain text pre-empts that fallback.
     */
    private _updateCellTooltips() {
        const dataItem = this.data
        // Index into `visibleDescriptors`, not `descriptors`: dataCells are created only
        // for non-hidden descriptors (CBTableRowView builds them from visibleDescriptors),
        // so once a table hides a column (`hidden: YES`) the full `descriptors` array no
        // longer lines up with the cells and the wrong descriptor's `tooltipText` would be
        // read — e.g. the "Pakkumusi" who/when tooltip in MyOngoingProcurementsViewController.
        // When nothing is hidden the two arrays are identical, so existing tables are unaffected.
        this.dataCells.forEach((cell, index) => {
            const descriptor = this.visibleDescriptors[index]
            const customText: string | undefined = dataItem
                ? descriptor?.tooltipText?.(dataItem) as string | undefined
                : undefined
            const button = cell.buttonView
            if (button) {
                cell.hoverText = undefined
                // A clickable (button-variant) cell that ALSO declares an explicit
                // descriptor tooltip — e.g. the "Pakkumusi" count with its who/when list —
                // gets the rich RH tooltip on its title label, exactly like a label cell.
                // Cells with no descriptor tooltip keep the lightweight native hover title
                // that only surfaces truncated button text. (No existing button cell
                // declares tooltipText, so this changes nothing for them.)
                if (customText !== undefined) {
                    button.hoverText = undefined
                    if (customText.length > 0 && customText !== "-") {
                        UITooltip.sharedInstance.attach(button.titleLabel, customText)
                    }
                    else {
                        UITooltip.sharedInstance.detach(button.titleLabel)
                    }
                }
                else {
                    button.hoverText = RHTooltip.plainTextFromMarkup(button.titleLabel.text)
                }
                return
            }
            const label = cell.titleLabel
            if (!label) {
                return
            }
            cell.hoverText = undefined
            const tooltipText = customText ?? RHTooltip.plainTextFromMarkup(label.text)
            if (tooltipText.length > 0 && tooltipText !== "-") {
                UITooltip.sharedInstance.attach(label, tooltipText)
            }
            else {
                UITooltip.sharedInstance.detach(label)
            }
        })
    }


    override layoutSubviews() {
        
        const isCardMode = this.usesCardMode && this.bounds.width > 0 && this.bounds.width < this.cardModeBreakpoint
        
        if (isCardMode) {
            this._layoutAsCard()
        }
        else {
            this._layoutAsTable()
        }
        
    }
    
    
    private _layoutAsTable() {
        this._cardLabelViews.everyElement.removeFromSuperview()
        if (this._usesCPVTreePresentation()) {
            this._layoutAsCPVTreeTable()
            return
        }
        this._clearCPVTreeConnectorViews()

        const disclosureColumnIndex = this._treeDisclosureColumnIndex()
        const disclosureColumnCell = this.dataCells[disclosureColumnIndex]
        const nestingDepth = this.data?._CB_DataView_NestingDepth ?? 0
        let disclosureColumnBaseLeftInset: number | undefined
        let disclosureColumnBaseRightInset: number | undefined

        if (disclosureColumnCell) {
            disclosureColumnBaseLeftInset = this._treeDisclosureColumnBaseLeftInset
                ?? disclosureColumnCell.contentInsets.left
            disclosureColumnBaseRightInset = this._treeDisclosureColumnBaseRightInset
                ?? disclosureColumnCell.contentInsets.right
            this._treeDisclosureColumnBaseLeftInset = disclosureColumnBaseLeftInset
            this._treeDisclosureColumnBaseRightInset = disclosureColumnBaseRightInset

            let contentLeftInset = disclosureColumnBaseLeftInset
            let contentRightInset = disclosureColumnBaseRightInset
            const hasDisclosureControl = this.expandabilityButtonView?.superview == this
            if (hasDisclosureControl || nestingDepth > 0) {
                const hierarchyContentInset = RHTables.rowEmphasizedDisclosureSize
                    + RHTables.rowDisclosureContentGap
                    + (nestingDepth * RHTables.rowDisclosureIndentStep)
                contentLeftInset += hierarchyContentInset
                if (
                    disclosureColumnCell.titleLabel?.textAlignment ==
                        UITextView.textAlignment.center
                ) {
                    contentRightInset += hierarchyContentInset
                }
            }
            if (
                disclosureColumnCell.contentInsets.left != contentLeftInset ||
                disclosureColumnCell.contentInsets.right != contentRightInset
            ) {
                disclosureColumnCell.contentInsets = {
                    ...disclosureColumnCell.contentInsets,
                    left: contentLeftInset,
                    right: contentRightInset
                }
            }
        }

        super.layoutSubviews()

        if (disclosureColumnCell && disclosureColumnBaseLeftInset !== undefined) {
            const firstCell = this.dataCells.firstElement
            const baseFirstCellFrame = this.contentBounds
                .rectanglesBySplittingWidth(this.cellWeights, this.padding, this.cellWidths)
                .firstElement

            if (firstCell && baseFirstCellFrame) {
                firstCell.frame = baseFirstCellFrame
                    .rectangleWithHeight(firstCell.frame.height)
                    .rectangleWithY(firstCell.frame.y)
            }

            this._layoutDisclosureControlInColumn(
                disclosureColumnCell,
                nestingDepth,
                disclosureColumnBaseLeftInset
            )
            return
        }

        this._layoutDefaultDisclosureControl()
    }


    _layoutDisclosureControlInColumn(
        disclosureColumnCell: UIView,
        nestingDepth: number,
        disclosureColumnBaseLeftInset: number
    ) {
        const disclosureControl = this.expandabilityButtonView
        if (!disclosureControl || disclosureControl.superview != this) {
            return
        }

        const disclosureX = disclosureColumnCell.frame.x
            + disclosureColumnBaseLeftInset
            + (nestingDepth * RHTables.rowDisclosureIndentStep)

        disclosureControl.weakFrame = disclosureColumnCell.frame
            .rectangleWithWidth(RHTables.rowEmphasizedDisclosureSize)
            .rectangleWithHeight(RHTables.rowEmphasizedDisclosureSize, 0.5)
            .rectangleWithX(disclosureX)
        disclosureControl.moveToTopOfSuperview()
    }


    _updateEmphasizedDisclosureControlPresentation() {
        const disclosureControl = this.expandabilityButtonView
        if (!disclosureControl) {
            return
        }

        const isExpanded = this.data?._CB_DataView_IsExpanded == YES
        const isExpandedAttributeValue = IF(isExpanded)(() => "true").ELSE(() => "false")
        let disclosureText = RHTables.rowEmphasizedDisclosureCollapsedIconMarkup
        let backgroundColor = RHTables.rowEmphasizedDisclosureBackgroundColor
        let borderColor = RHTables.rowEmphasizedDisclosureBorderColor

        if (isExpanded) {
            disclosureText = RHTables.rowEmphasizedDisclosureExpandedIconMarkup
            backgroundColor = RHTables.rowEmphasizedDisclosureExpandedBackgroundColor
            borderColor = RHTables.rowEmphasizedDisclosureExpandedBorderColor
        }

        disclosureControl.viewHTMLElement.classList.remove("RHTableDisclosureButton")
        disclosureControl.viewHTMLElement.setAttribute("data-expanded", isExpandedAttributeValue)
        disclosureControl.viewHTMLElement.setAttribute("aria-expanded", isExpandedAttributeValue)
        disclosureControl.viewHTMLElement.setAttribute("role", "button")

        disclosureControl.configureWithObject({
            text: disclosureText,
            isSingleLine: YES,
            textAlignment: UITextView.textAlignment.center,
            textColor: RHTables.rowEmphasizedDisclosureTextColor,
            style: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxSizing: "border-box",
                overflow: "hidden",
                userSelect: "none",
                cursor: "pointer",
                lineHeight: `${RHTables.rowEmphasizedDisclosureSize}px`,
                borderRadius: RHTables.rowEmphasizedDisclosureBorderRadius,
                border: `1px solid ${borderColor.stringValue}`,
                backgroundColor: backgroundColor.stringValue,
                boxShadow: RHTables.rowEmphasizedDisclosureBoxShadow,
                transition: RHTables.rowEmphasizedDisclosureTransition
            }
        })
    }


    private _layoutDefaultDisclosureControl() {
        const disclosureControl = this.expandabilityButtonView
        if (!disclosureControl || disclosureControl.superview != this) {
            return
        }

        RHTableRowView._injectDefaultDisclosureCSS()

        const nestingDepth = this.data?._CB_DataView_NestingDepth ?? 0
        const disclosureSize = RHTables.rowDisclosureSize
        const firstCell = this.dataCells.firstElement
        const baseFirstCellFrame = this.contentBounds
            .rectanglesBySplittingWidth(this.cellWeights, this.padding, this.cellWidths)
            .firstElement

        if (firstCell && baseFirstCellFrame && nestingDepth === 0) {
            firstCell.frame = baseFirstCellFrame
                .rectangleWithHeight(firstCell.frame.height)
                .rectangleWithY(firstCell.frame.y)
        }

        const firstCellX = firstCell?.frame.x ?? this.contentInsets.left
        const disclosureX = Math.max(
            0,
            firstCellX - disclosureSize - RHTables.rowDisclosureContentGap +
            (nestingDepth * RHTables.rowDisclosureIndentStep)
        )
        const isExpanded = this.data?._CB_DataView_IsExpanded == YES
        const isExpandedAttributeValue = IF(isExpanded)(() => "true").ELSE(() => "false")

        if (disclosureControl.text) {
            disclosureControl.text = ""
        }

        disclosureControl.viewHTMLElement.classList.add("RHTableDisclosureButton")
        disclosureControl.viewHTMLElement.setAttribute("data-expanded", isExpandedAttributeValue)
        disclosureControl.viewHTMLElement.setAttribute("aria-expanded", isExpandedAttributeValue)
        disclosureControl.viewHTMLElement.setAttribute("role", "button")

        disclosureControl.configureWithObject({
            isSingleLine: YES,
            textAlignment: UITextView.textAlignment.center,
            textColor: RHTables.rowDisclosureTextColor,
            style: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxSizing: "border-box",
                overflow: "hidden",
                userSelect: "none",
                cursor: "pointer",
                fontSize: RHTables.rowDisclosureFontSize,
                fontWeight: RHTables.rowDisclosureFontWeight,
                lineHeight: `${disclosureSize}px`,
                borderRadius: RHTables.rowDisclosureBorderRadius,
                border: `1px solid ${RHTables.rowDisclosureBorderColor.stringValue}`,
                backgroundColor: RHTables.rowDisclosureBackgroundColor.stringValue,
                boxShadow: RHTables.rowDisclosureBoxShadow,
                transition: "background-color 0.14s ease, border-color 0.14s ease, color 0.14s ease"
            }
        })

        disclosureControl.weakFrame = this.bounds
            .rectangleWithWidth(disclosureSize)
            .rectangleWithHeight(disclosureSize, 0.5)
            .rectangleWithX(disclosureX)

        disclosureControl.moveToTopOfSuperview()
    }

    static _injectDefaultDisclosureCSS() {

        const chevronLeft = (RHTables.rowDisclosureSize - RHTables.rowDisclosureChevronSize) / 2
        const chevronTop = (RHTables.rowDisclosureSize - RHTables.rowDisclosureChevronStrokeWidth) / 2
        const collapsedChevronOffset = RHTables.rowDisclosureChevronSize * 0.25
        const expandedLeftChevronOffset = RHTables.rowDisclosureChevronSize * 0.25
        const expandedRightChevronOffset = RHTables.rowDisclosureChevronSize * 0.375

        UIView.injectCSS(`
            .RHTableDisclosureButton {
                color: ${RHTables.rowDisclosureTextColor.stringValue};
            }
            .RHTableDisclosureButton::before,
            .RHTableDisclosureButton::after {
                content: "";
                display: block;
                position: absolute;
                width: ${RHTables.rowDisclosureChevronSize}px;
                height: ${RHTables.rowDisclosureChevronStrokeWidth}px;
                left: ${chevronLeft}px;
                top: ${chevronTop}px;
                background-color: currentColor;
                border-radius: 999px;
                transform-origin: center;
                transition: transform 0.14s ease;
            }
            .RHTableDisclosureButton::before {
                transform: translateY(-${collapsedChevronOffset}px) rotate(45deg);
            }
            .RHTableDisclosureButton::after {
                transform: translateY(${collapsedChevronOffset}px) rotate(-45deg);
            }
            .RHTableDisclosureButton[data-expanded="true"] {
                color: ${RHTables.rowDisclosureExpandedTextColor.stringValue} !important;
                background-color: ${RHTables.rowDisclosureExpandedBackgroundColor.stringValue} !important;
            }
            .RHTableDisclosureButton[data-expanded="true"]::before {
                transform: translateX(-${expandedLeftChevronOffset}px) rotate(45deg);
            }
            .RHTableDisclosureButton[data-expanded="true"]::after {
                transform: translateX(${expandedRightChevronOffset}px) rotate(-45deg);
            }
            .RHTableDisclosureButton:hover,
            .RHTableDisclosureButton:focus-visible {
                color: ${RHTables.rowDisclosureHoverTextColor.stringValue} !important;
                background-color: ${RHTables.rowDisclosureHoverBackgroundColor.stringValue} !important;
                border-color: ${RHTables.rowDisclosureHoverBorderColor.stringValue} !important;
            }
        `)
    }


    private _layoutAsCard() {
        this._clearCPVTreeConnectorViews()

        UIView.prototype.layoutSubviews.call(this)

        this._cardLabelViews.everyElement.addedAsSubviewToView(this)
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)
        
        const labelColumnWidth = Math.min(bounds.width * 0.38, 180)
        const cellColumnWidth = bounds.width - labelColumnWidth - padding
        const rowGap = padding * 0.4
        const minRowHeight = RHTables.rowMinHeight * 0.6
        
        let currentY = bounds.y
        
        this.dataCells.forEach((cellView, index) => {
            
            const labelView = this._cardLabelViews[index]
            const titleString = labelView?.text ?? ""
            const hasLabel = titleString.trim().length > 0
            
            if (hasLabel && labelView) {
                
                const h = Math.max(
                    cellView.intrinsicContentHeight(cellColumnWidth),
                    minRowHeight
                )
                
                bounds.rectangleWithY(currentY).rectangleWithHeight(h)
                    .distributeViewsAlongWidth(
                        [labelView, cellView],
                        undefined,
                        padding,
                        [labelColumnWidth, nil]
                    )
                
                currentY += h + rowGap
                
            }
            else {
                
                const hasContent = cellView.customView != null
                    || (cellView.variant == "label" && (cellView.titleLabel?.text ?? "").length > 0)
                    || (cellView.variant == "button" && (cellView.buttonView?.titleLabel?.text ?? "").length > 0)
                
                if (labelView) {
                    labelView.frame = labelView.frame.rectangleWithHeight(0).rectangleWithWidth(0)
                }
                
                if (hasContent) {
                    const h = Math.max(
                        cellView.intrinsicContentHeight(bounds.width),
                        minRowHeight
                    )
                    cellView.frame = bounds.rectangleWithY(currentY).rectangleWithHeight(h)
                    currentY += h + rowGap
                }
                else {
                    cellView.frame = bounds.rectangleWithY(currentY).rectangleWithHeight(0)
                }
                
            }
            
        })
        
        this.backgroundView.weakFrame = this.bounds.rectangleWithInsets(
            padding * 0.5, padding * 0.5, 0, 0
        )
        this._bottomLineView.weakFrame = this.bounds
            .rectangleWithHeight(1, 1)
            .rectangleWithInsets(padding * 0.5, padding * 0.5, 0, 0)
        
        if (this.highlightingBorderView) {
            this.highlightingBorderView.weakFrame = this.bounds
        }
        
    }
    
    
    _usesCPVTreePresentation() {
        return this.viewHTMLElement.dataset.rhTreeVariant == "cpv"
    }
    
    
    _ensureCPVTreeConnectorViews() {
        this._treeConnectorStemView!.hidden = NO
        this._treeConnectorHorizontalView!.hidden = NO
    }
    
    
    _clearCPVTreeConnectorViews() {
        this._treeConnectorStemView!.hidden = YES
        this._treeConnectorHorizontalView!.hidden = YES
    }


    _cpvTreeDisclosureX(nestingDepth: number) {
        return (nestingDepth * RHTables.treeIndentStep) + RHTables.treeExpanderLeadingInset
    }


    _cpvTreeContentInset(nestingDepth: number) {
        return (nestingDepth * RHTables.treeIndentStep) + RHTables.treeExpanderReservedColumnWidth
    }
    
    
    _layoutAsCPVTreeTable() {
        const nestingDepth = this.data?._CB_DataView_NestingDepth ?? 0
        const padding = this.core.paddingLength
        
        this.firstCellLeftInset = this._cpvTreeContentInset(nestingDepth)
        
        RowView.prototype.layoutSubviews.call(this)
        
        this.backgroundView.weakFrame = this.bounds.rectangleWithInsets(padding, padding, 0, 0)
        this._bottomLineView.weakFrame = this.bounds.rectangleWithHeight(1, 1)
            .rectangleWithInsets(padding, padding, 0, 0)
        
        if (this.highlightingBorderView) {
            this.highlightingBorderView.weakFrame = this.bounds
        }
        
        this._layoutCPVDisclosureControl(nestingDepth)
        this._layoutCPVConnectorViews(nestingDepth)
    }
    
    
    _layoutCPVDisclosureControl(nestingDepth: number) {
        const disclosureControl = this._updateCPVDisclosureControlPresentation()
        if (!disclosureControl) {
            return
        }

        disclosureControl.weakFrame = this.bounds
            .rectangleWithWidth(RHTables.treeExpanderSize)
            .rectangleWithHeight(RHTables.treeExpanderSize, 0.5)
            .rectangleByAddingX(this._cpvTreeDisclosureX(nestingDepth))
        disclosureControl.moveToTopOfSuperview()
    }


    _updateCPVDisclosureControlPresentation() {
        const disclosureControl = this.expandabilityButtonView
        if (!disclosureControl || disclosureControl.superview != this) {
            return nil
        }

        disclosureControl.viewHTMLElement.classList.remove("RHTableDisclosureButton")
        
        let backgroundColor = RHTables.treeExpanderCollapsedBackgroundColor
        let borderColor = RHTables.treeExpanderCollapsedBorderColor
        let disclosureText = "+"
        
        if (this.data?._CB_DataView_IsExpanded) {
            backgroundColor = RHTables.treeExpanderExpandedBackgroundColor
            borderColor = RHTables.treeExpanderExpandedBorderColor
            disclosureText = "&#8722;"
        }
        
        const presentationKey = `${disclosureText}_${backgroundColor.stringValue}_${borderColor.stringValue}`
        
        if (disclosureControl.text != disclosureText) {
            disclosureControl.text = disclosureText
        }
        
        if (this._cpvDisclosurePresentationKey != presentationKey) {
            disclosureControl.configureWithObject({
                isSingleLine: YES,
                textAlignment: UITextView.textAlignment.center,
                textColor: RHTables.treeExpanderTextColor,
                style: {
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    userSelect: "none",
                    fontSize: RHTables.treeExpanderFontSize,
                    fontWeight: RHTables.treeExpanderFontWeight,
                    lineHeight: `${RHTables.treeExpanderSize}px`,
                    borderRadius: RHTables.treeExpanderBorderRadius,
                    border: `1px solid ${borderColor.stringValue}`,
                    backgroundColor: backgroundColor.stringValue,
                    boxShadow: RHTables.treeExpanderShadow,
                    transition: RHTables.treeExpanderTransition
                }
            })
            this._cpvDisclosurePresentationKey = presentationKey
        }

        return disclosureControl
    }
    
    
    _layoutCPVConnectorViews(nestingDepth: number) {
        const hasDisclosureControl = this.expandabilityButtonView?.superview == this
        const isExpanded = this.data?._CB_DataView_IsExpanded == YES
        
        if (nestingDepth <= 0 && !(hasDisclosureControl && isExpanded)) {
            this._clearCPVTreeConnectorViews()
            return
        }
        
        this._ensureCPVTreeConnectorViews()
        
        const disclosureLeft = this._cpvTreeDisclosureX(nestingDepth)
        const disclosureCenterX = disclosureLeft + (RHTables.treeExpanderSize * 0.5)
        
        if (nestingDepth > 0) {
            const parentConnectorX = ((nestingDepth - 1) * RHTables.treeIndentStep)
                + RHTables.treeExpanderLeadingInset
                + (RHTables.treeExpanderSize * 0.5)
            const connectorStemX = IF(hasDisclosureControl && isExpanded)(() =>
                disclosureCenterX
            ).ELSE(() =>
                parentConnectorX
            )
            const connectorHorizontalWidth = Math.max(
                disclosureLeft - parentConnectorX,
                RHTables.treeConnectorHorizontalLength
            )
            const connectorStemHeight = IF(hasDisclosureControl && isExpanded)(() =>
                this.bounds.height
            ).ELSE(() =>
                this.bounds.height * 0.5
            )
            
            this._treeConnectorStemView!.weakFrame = this.bounds
                .rectangleWithWidth(1)
                .rectangleWithHeight(connectorStemHeight)
                .rectangleByAddingX(connectorStemX)
            
            this._treeConnectorHorizontalView!.weakFrame = this.bounds
                .rectangleWithHeight(1, 0.5)
                .rectangleWithWidth(connectorHorizontalWidth)
                .rectangleByAddingX(parentConnectorX)
        }
        else {
            this._treeConnectorStemView!.weakFrame = this.bounds
                .rectangleWithWidth(1)
                .rectangleWithHeight(this.bounds.height * 0.5, 1)
                .rectangleByAddingX(
                    disclosureCenterX
                )
            this._treeConnectorHorizontalView!.hidden = YES
            this._treeConnectorHorizontalView!.weakFrame = this.bounds
                .rectangleWithWidth(0)
                .rectangleWithHeight(0)
        }
    }
    
    
}
